/*
  Santara v28 (15/10/2026) — panier A « Le panier clarifié » (maquette Claude Design « Santara — Panier.pdf », go Rayan).
  Chargé par snippets/santara-global.liquid. Tiroir (Horizon cart-drawer-section) et page /cart (main-cart).
  1. Feuille « Changer de pointure » (A3) : ouverte par « Pointure 38 · Modifier » (snippets/santara-cart-pointure.liquid),
     pointures de la même couleur lues dans les variantes ; validation = UNE requête /cart/update.js
     ({ancienne ligne: 0, nouvelle variante: même quantité} + sections du panier), puis rafraîchissement comme Horizon
     (CartLinesUpdateEvent → cart-items-component re-rend la section). Erreur : message, rien ne change.
  2. « Guide des tailles » : la page /pages/guide-des-tailles chargée dans un panneau par-dessus le panier.
  3. « Conditions » (retours) dépliable. 4. Barre collée /cart à partir de 2 articles (IntersectionObserver).
  5. Ordinateur : voile sur la page quand le tiroir est ouvert (clic = fermer). 6. Événements Clarity, sans donnée personnelle.
*/
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const ERR_GEN = 'Impossible de mettre à jour le panier. Réessayez.';
const ERR_DISPO = "Cette pointure n'est plus disponible. Choisissez-en une autre.";
const SVG_X = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4.5 4.5l11 11M15.5 4.5l-11 11"/></svg>';
const SVG_CHEV = '<svg class="sa-chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4l4 4-4 4"/></svg>';
const SVG_RULE = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="5" width="13" height="6"/><path d="M4.5 5v2.5M7 5v3.5M9.5 5v2.5M12 5v3.5"/></svg>';
const SVG_MSG = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 3h12v8H6l-3 2.5V11H2z"/></svg>';

function ev(name) {
  try { if (window.clarity) window.clarity('event', name); } catch (e) { /* mesure seulement */ }
}
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}
/** Le panneau du tiroir (dialog Horizon) si l'élément y est, sinon la page. */
function hostOf(el) {
  return (el && el.closest && el.closest('#cart-drawer dialog.theme-drawer__dialog')) || document.body;
}
function liveOf(host) {
  let l = host.querySelector(':scope > .sa-live');
  if (!l) {
    l = document.createElement('div');
    l.className = 'sa-live';
    l.setAttribute('role', 'status');
    l.setAttribute('aria-live', 'polite');
    host.appendChild(l);
  }
  return l;
}
function announce(host, text) {
  const l = liveOf(host);
  l.textContent = '';
  setTimeout(() => { l.textContent = text; }, 60);
}

/* ---------- feuilles (dialog modal, en bas de l'écran / en bas du tiroir) ---------- */
function closeSheet(d, restore = true) {
  if (!d || !d.open) return;
  d.close();
  const o = d._opener;
  d._opener = null;
  if (restore && o && document.contains(o)) {
    try { o.focus({ preventScroll: true }); } catch (e) { o.focus(); }
  }
}
function makeSheet(host, cls) {
  const d = document.createElement('dialog');
  d.className = 'sa-sheet ' + cls + (host === document.body ? ' sa-sheet--page' : '');
  host.appendChild(d);
  d.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeSheet(d);
    }
  });
  d.addEventListener('cancel', (e) => { e.preventDefault(); closeSheet(d); });
  d.addEventListener('click', (e) => {
    if (e.target !== d) return;
    const r = d.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
      e.stopPropagation(); // le tiroir ne doit pas se fermer avec la feuille
      closeSheet(d);
    }
  });
  return d;
}

/* ---------- 1. feuille des pointures ---------- */
function sizeSheet(host) {
  let d = host.querySelector(':scope > dialog.sa-sheet--pt');
  if (d) return d;
  d = makeSheet(host, 'sa-sheet--pt');
  d.setAttribute('aria-labelledby', 'sa-pt-h-' + (host === document.body ? 'p' : 'd'));
  d.innerHTML =
    '<div class="sa-sheet-hd"><div><h2 class="sa-sheet-t" id="' + d.getAttribute('aria-labelledby') + '"></h2><p class="sa-sheet-st"></p></div>' +
    '<button type="button" class="sa-sheet-x" aria-label="Fermer">' + SVG_X + '</button></div>' +
    '<div class="sa-pt-grid" role="radiogroup"></div>' +
    '<div class="sa-help">' +
    '<button type="button" class="sa-help-row sa-help-guide">' + SVG_RULE + '<span>Guide des tailles</span>' + SVG_CHEV + '</button>' +
    '<button type="button" class="sa-help-row sa-help-conseil" aria-expanded="false">' + SVG_MSG + '<span>Un doute ? Conseil par SMS ou par e-mail</span>' + SVG_CHEV + '</button>' +
    '<div class="sa-help-links" hidden><a href="sms:+33695743756">Par SMS · 06 95 74 37 56</a><a href="mailto:contact@santara.fr">Par e-mail · contact@santara.fr</a></div>' +
    '</div>' +
    '<p class="sa-sheet-ex">Nouvelle pointure réexpédiée sans frais sous 30 jours. Le renvoi de la première paire reste à votre charge.</p>' +
    '<p class="sa-sheet-err" role="alert" hidden></p>' +
    '<button type="button" class="sa-sheet-ok"></button>';
  d.querySelector('.sa-sheet-x').addEventListener('click', () => closeSheet(d));
  d.querySelector('.sa-help-guide').addEventListener('click', (e) => openGuide(e.currentTarget, d._data && d._data.guide));
  d.querySelector('.sa-help-conseil').addEventListener('click', (e) => {
    const b = e.currentTarget;
    const open = b.getAttribute('aria-expanded') !== 'true';
    b.setAttribute('aria-expanded', String(open));
    d.querySelector('.sa-help-links').hidden = !open;
  });
  const grid = d.querySelector('.sa-pt-grid');
  grid.addEventListener('click', (e) => {
    const o = e.target.closest('.sa-pt-o');
    if (!o || d._busy || o.getAttribute('aria-disabled') === 'true') return;
    choose(d, o.dataset.id, false);
  });
  grid.addEventListener('keydown', (e) => {
    const o = e.target.closest('.sa-pt-o');
    if (!o) return;
    if (e.key === 'Enter') { e.preventDefault(); validate(d); return; }
    if (e.key === ' ') { e.preventDefault(); if (o.getAttribute('aria-disabled') !== 'true') choose(d, o.dataset.id, true); return; }
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const all = Array.from(grid.querySelectorAll('.sa-pt-o')).filter((x) => x.getAttribute('aria-disabled') !== 'true');
    if (!all.length) return;
    let i = all.indexOf(o);
    i = (i + step + all.length) % all.length;
    choose(d, all[i].dataset.id, true);
  });
  d.querySelector('.sa-sheet-ok').addEventListener('click', () => validate(d));
  return d;
}

function okLabel(d) {
  const data = d._data;
  const s = data.sizes.find((x) => String(x.id) === String(d._choice));
  const t = s ? s.t : '';
  const o = data.opt.toLowerCase();
  return String(d._choice) === String(data.variant) ? 'Garder la ' + o + ' ' + t : 'Valider la ' + o + ' ' + t;
}

function choose(d, id, focus) {
  d._choice = String(id);
  d.querySelectorAll('.sa-pt-o').forEach((x) => {
    const on = x.dataset.id === d._choice;
    x.setAttribute('aria-checked', on ? 'true' : 'false');
    x.tabIndex = on ? 0 : -1;
    if (on && focus) x.focus();
  });
  const err = d.querySelector('.sa-sheet-err');
  err.hidden = true;
  d.querySelector('.sa-sheet-ok').textContent = okLabel(d);
}

function openSizes(btn) {
  let sizes = [];
  try { sizes = JSON.parse(btn.dataset.sizes || '[]'); } catch (e) { sizes = []; }
  if (!sizes.length) return;
  const host = hostOf(btn);
  const d = sizeSheet(host);
  const data = {
    key: btn.dataset.key,
    variant: String(btn.dataset.variant),
    qty: parseInt(btn.dataset.qty || '1', 10) || 1,
    nom: btn.dataset.nom || '',
    couleur: btn.dataset.couleur || '',
    opt: btn.dataset.opt || 'Pointure',
    guide: btn.dataset.guide || '/pages/guide-des-tailles',
    echange: btn.dataset.echange === '1',
    sizes,
  };
  d._data = data;
  d._opener = btn;
  d._busy = false;
  d.querySelector('.sa-sheet-t').textContent = 'Changer de ' + data.opt.toLowerCase();
  d.querySelector('.sa-sheet-st').textContent = [data.nom, data.couleur].filter(Boolean).join(' · ');
  d.querySelector('.sa-sheet-ex').hidden = !data.echange;
  d.querySelector('.sa-help-links').hidden = true;
  d.querySelector('.sa-help-conseil').setAttribute('aria-expanded', 'false');
  const grid = d.querySelector('.sa-pt-grid');
  grid.setAttribute('aria-label', data.opt);
  grid.innerHTML = sizes
    .map((s) => {
      const cur = String(s.id) === data.variant;
      const dis = !s.a && !cur;
      const note = cur ? 'actuelle' : dis ? 'indisponible' : '';
      return (
        '<button type="button" role="radio" class="sa-pt-o" data-id="' + esc(s.id) + '" aria-checked="false" tabindex="-1"' +
        (dis ? ' aria-disabled="true"' : '') +
        ' aria-label="' + esc(s.t + (note ? ', ' + note : '')) + '">' +
        '<span class="sa-pt-n">' + esc(s.t) + '</span>' + (note ? '<small>' + note + '</small>' : '') + '</button>'
      );
    })
    .join('');
  const okb = d.querySelector('.sa-sheet-ok');
  okb.disabled = false;
  okb.removeAttribute('aria-busy');
  choose(d, data.variant, false);
  d.showModal();
  const cur = grid.querySelector('[aria-checked="true"]');
  if (cur) cur.focus();
  ev('panier_pointure_ouverte');
}

function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((c) => {
    const s = c.dataset.sectionId;
    if (s && ids.indexOf(s) < 0) ids.push(s);
  });
  return ids;
}
function setBusy(d, on) {
  d._busy = on;
  const okb = d.querySelector('.sa-sheet-ok');
  okb.disabled = on;
  if (on) {
    okb.setAttribute('aria-busy', 'true');
    okb.innerHTML = '<span class="sa-spin" aria-hidden="true"></span><span>Mise à jour…</span>';
  } else {
    okb.removeAttribute('aria-busy');
    okb.textContent = okLabel(d);
  }
  document.querySelectorAll('cart-items-component').forEach((c) => c.classList.toggle('sa-busy', on));
}

/** Après une erreur réseau : on relit le panier et on re-rend les sections, pour qu'aucune ligne ne reste dans un état inconnu. */
async function resync(src) {
  const ids = sectionIds();
  try {
    const [cart, sections] = await Promise.all([
      fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(','))).then((r) => r.json()),
    ]);
    const p = CartLinesUpdateEvent.createPromise();
    src.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: p.promise }));
    p.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'santara-pointure', didError: false },
    });
  } catch (e) { /* le panier reste tel qu'affiché */ }
}

async function validate(d) {
  const data = d._data;
  if (!data || d._busy) return;
  if (String(d._choice) === data.variant) { closeSheet(d); return; }
  const choice = String(d._choice);
  const updates = {};
  updates[data.key] = 0;
  let existing = null;
  document.querySelectorAll('.sa-row[data-variant-id="' + choice + '"]').forEach((r) => {
    if (!existing && r.dataset.key && r.dataset.key !== data.key) existing = r;
  });
  if (existing) updates[existing.dataset.key] = (parseInt(existing.dataset.qty || '0', 10) || 0) + data.qty;
  else updates[choice] = data.qty;

  setBusy(d, true);
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  d.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: data.key, quantity: 0 }], // même forme que les mises à jour Horizon (la nouvelle ligne arrive avec le panier renvoyé)
      promise: deferred.promise,
    })
  );
  let res = null;
  let json = null;
  try {
    res = await fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: ids.join(','), sections_url: window.location.pathname }),
    });
    json = await res.json();
  } catch (e) { json = null; }

  if (!res || !res.ok || !json || json.status || json.errors) {
    const dispo = !!(res && (res.status === 422 || res.status === 404));
    const msg = dispo ? ERR_DISPO : ERR_GEN;
    deferred.reject(Object.assign(new Error(msg), { name: 'AbortError' }));
    d.dispatchEvent(new CartErrorEvent({ error: msg, code: dispo ? 'INVALID' : 'SERVICE_UNAVAILABLE' }));
    setBusy(d, false);
    const err = d.querySelector('.sa-sheet-err');
    err.textContent = msg;
    err.hidden = false;
    if (!res || !json) resync(d);
    return;
  }

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
    detail: { sections: json.sections, items: json.items, itemCount: json.item_count, source: 'santara-pointure', didError: false },
  });
  ev('panier_pointure_modifiee');
  const host = hostOf(d);
  const label = (data.sizes.find((x) => String(x.id) === choice) || {}).t || '';
  setBusy(d, false);
  closeSheet(d, false);
  announce(host, data.opt + ' modifiée : ' + label);
  // la ligne re-rendue est surlignée 600 ms (#F4EEE3) et reprend le focus
  const t0 = Date.now();
  (function wait() {
    const row = document.querySelector('.sa-row[data-variant-id="' + choice + '"]');
    if (row && !document.querySelector('.sa-row[data-key="' + CSS.escape(data.key) + '"]')) {
      row.classList.add('sa-row-flash');
      setTimeout(() => row.classList.remove('sa-row-flash'), 600);
      const b = row.querySelector('[data-sa-pt-open]') || row.querySelector('a');
      if (b) { try { b.focus({ preventScroll: true }); } catch (e) { b.focus(); } }
      return;
    }
    if (Date.now() - t0 < 3000) requestAnimationFrame(wait);
  })();
}

/* ---------- 2. guide des tailles ---------- */
const guideCache = {};
function guideSheet(host) {
  let g = host.querySelector(':scope > dialog.sa-sheet--guide');
  if (g) return g;
  g = makeSheet(host, 'sa-sheet--guide');
  const hid = 'sa-gd-h-' + (host === document.body ? 'p' : 'd');
  g.setAttribute('aria-labelledby', hid);
  g.innerHTML =
    '<div class="sa-sheet-hd"><div><h2 class="sa-sheet-t" id="' + hid + '">Guide des tailles</h2></div>' +
    '<button type="button" class="sa-sheet-x" aria-label="Fermer">' + SVG_X + '</button></div><div class="sa-gd-body"></div>';
  g.querySelector('.sa-sheet-x').addEventListener('click', () => closeSheet(g));
  return g;
}
async function openGuide(trigger, urlOverride) {
  const url = urlOverride || (trigger && (trigger.dataset.saGuide || trigger.getAttribute('href'))) || '/pages/guide-des-tailles';
  const host = hostOf(trigger);
  const g = guideSheet(host);
  g._opener = trigger;
  const body = g.querySelector('.sa-gd-body');
  body.innerHTML = '<p class="sa-gd-load"><span class="sa-spin" aria-hidden="true"></span> Chargement…</p>';
  if (!g.open) g.showModal();
  g.querySelector('.sa-sheet-x').focus();
  ev('panier_guide_tailles');
  try {
    let html = guideCache[url];
    if (!html) {
      const r = await fetch(url, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('guide');
      html = await r.text();
      guideCache[url] = html;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const c =
      doc.querySelector('#MainContent .page-content rte-formatter') ||
      doc.querySelector('#MainContent .page-content') ||
      doc.querySelector('#MainContent .rte');
    if (!c) throw new Error('guide');
    c.querySelectorAll('script, style, iframe, form').forEach((n) => n.remove());
    const h = c.querySelector('h1, h2');
    if (h && /guide des tailles/i.test(h.textContent || '') && h === c.firstElementChild) h.remove();
    body.innerHTML = c.innerHTML;
  } catch (e) {
    body.innerHTML = '<p>Le guide n\'a pas pu être chargé. <a href="' + esc(url) + '">Ouvrir le guide des tailles</a></p>';
  }
}

/* ---------- 3. clics délégués : pointure, guide, conditions, finaliser, voile ---------- */
document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const ptb = t.closest('[data-sa-pt-open]');
  if (ptb) { e.preventDefault(); openSizes(ptb); return; }
  const gl = t.closest('a[data-sa-guide]');
  if (gl) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
    e.preventDefault();
    openGuide(gl);
    return;
  }
  const cb = t.closest('.sa-cond-btn');
  if (cb) {
    const p = document.getElementById(cb.getAttribute('aria-controls') || '');
    if (!p) return;
    const open = cb.getAttribute('aria-expanded') !== 'true';
    cb.setAttribute('aria-expanded', String(open));
    p.hidden = !open;
    if (open) ev('panier_conditions_ouvertes');
    return;
  }
  const fin = t.closest('.cart__checkout-button, .sa-bar-btn');
  if (fin && !fin.disabled) {
    ev('panier_finaliser');
    // après le clic (la soumission du formulaire part d'abord) : « Ouverture du paiement… »
    setTimeout(() => {
      if (!document.contains(fin)) return;
      fin.dataset.saLabel = fin.dataset.saLabel || fin.textContent.trim();
      fin.classList.add('sa-going');
      fin.innerHTML = '<span class="sa-spin" aria-hidden="true"></span><span class="button-text">Ouverture du paiement…</span>';
    }, 0);
    return;
  }
  // ordinateur : clic sur le voile (pseudo-élément de .page-wrapper) = fermer le tiroir
  if (t.classList.contains('page-wrapper--drawer-open') && window.matchMedia('(min-width: 990px)').matches) {
    const dr = document.getElementById('cart-drawer');
    if (dr && dr.hasAttribute('open') && typeof dr.close === 'function') dr.close();
  }
});
// retour arrière depuis le paiement (cache du navigateur) : le bouton reprend son libellé
window.addEventListener('pageshow', (e) => {
  if (!e.persisted) return;
  document.querySelectorAll('.sa-going').forEach((b) => {
    b.classList.remove('sa-going');
    b.innerHTML = '<span class="button-text">' + esc(b.dataset.saLabel || 'Finaliser ma commande') + '</span>';
  });
});

/* ---------- 4. barre collée de la page /cart (2 articles ou plus) ---------- */
let io = null;
let ioTarget = null;
let barOn = false;
function paintBar() {
  const bar = document.querySelector('[data-sa-bar]');
  document.documentElement.classList.toggle('sa-bar-vis', !!(bar && barOn));
  if (!bar) return;
  bar.classList.toggle('is-on', barOn);
  bar.setAttribute('aria-hidden', barOn ? 'false' : 'true');
  const b = bar.querySelector('.sa-bar-btn');
  if (b) b.tabIndex = barOn ? 0 : -1;
}
function setupBar() {
  const bar = document.querySelector('[data-sa-bar]');
  const target = document.querySelector('.cart-page__summary .cart__checkout-button');
  if (!bar || !target || !('IntersectionObserver' in window)) {
    if (io) io.disconnect();
    io = null;
    ioTarget = null;
    barOn = false;
    paintBar();
    return;
  }
  if (target !== ioTarget) {
    if (io) io.disconnect();
    io = new IntersectionObserver((en) => {
      const last = en[en.length - 1];
      barOn = !last.isIntersecting;
      paintBar();
    });
    io.observe(target);
    ioTarget = target;
  }
  paintBar();
}
function watchCartPage() {
  const page = document.querySelector('.cart-page');
  if (!page) return;
  setupBar();
  const comp = page.closest('cart-items-component') || page.parentElement;
  let raf = 0;
  new MutationObserver(() => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; setupBar(); });
  }).observe(comp, { childList: true, subtree: true });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCartPage);
else watchCartPage();
